import { APP_NAME } from '@clanfire/shared';

/**
 * A calendar file (.ics, RFC 5545) for one event, so people can add it to
 * Google Calendar, Outlook or Apple Calendar. Times are written in UTC; every
 * calendar app shows them in the person's own time zone.
 */
export interface CalendarEvent {
  id: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string | null;
  where: string | null;
  url: string;
}

/** 20261009T120000Z */
const utc = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

/** Backslashes, commas, semicolons and line breaks have to be escaped in text values. */
const text = (value: string) => value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Lines longer than 75 bytes are folded: a line break, then a space (RFC 5545 §3.1). */
function fold(line: string): string {
  const parts: string[] = [];
  let current = '';
  let bytes = 0;
  for (const char of line) {
    const size = new TextEncoder().encode(char).length;
    if (bytes + size > 74) {
      parts.push(current);
      current = ' ';
      bytes = 1;
    }
    current += char;
    bytes += size;
  }
  parts.push(current);
  return parts.join('\r\n');
}

/** Events without an end are put in calendars as an hour long. */
const DEFAULT_LENGTH_MS = 60 * 60_000;

export function eventToIcs(event: CalendarEvent, now = new Date()): string {
  const end = event.endsAt ?? new Date(Date.parse(event.startsAt) + DEFAULT_LENGTH_MS).toISOString();
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${APP_NAME}//Events//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@${APP_NAME.toLowerCase()}`,
    `DTSTAMP:${utc(now.toISOString())}`,
    `DTSTART:${utc(event.startsAt)}`,
    `DTEND:${utc(end)}`,
    `SUMMARY:${text(event.name)}`,
    ...(event.description ? [`DESCRIPTION:${text(`${event.description}\n\n${event.url}`)}`] : [`DESCRIPTION:${text(event.url)}`]),
    ...(event.where ? [`LOCATION:${text(event.where)}`] : []),
    `URL:${event.url}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.map(fold).join('\r\n') + '\r\n';
}

/** Saves the event as a .ics file, which opens in the person's calendar app. */
export function downloadIcs(event: CalendarEvent) {
  const blob = new Blob([eventToIcs(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${event.name.replace(/[^\p{L}\p{N} _-]+/gu, '').trim().slice(0, 60) || 'event'}.ics`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
